import { Injectable, inject } from '@angular/core';

import { Auth } from 'src/providers/auth/auth';
import { Client } from 'src/providers/client/client';
import { Genome } from 'src/providers/genome/genome';

import { ConciergeQueriesService } from 'src/shared/services/concierge-queries.service';
import { LlmService } from 'src/shared/services/llm.service';

import { executeToolCall } from 'src/shared/components/gemini-live/gemini-live-tools';

export interface ToolCallResult {
  functionResponses: { id: string; name: string; response: { result: string } }[];
  uiAction?: 'prompt_password_change' | 'confirm_add_asset' | 'send_transcript' | 'log_transcript' | 'get_conversation_history';
  uiActionToolCallId?: string;
  uiActionData?: any;
}

@Injectable({
  providedIn: 'root',
})
export class GeminiToolHandlerService {
  private readonly auth = inject(Auth);
  private readonly client = inject(Client);
  private readonly conciergeQueries = inject(ConciergeQueriesService);
  private readonly genome = inject(Genome);
  private readonly llm = inject(LlmService);

  private voiceSessionTicketId: number | null = null;

  setVoiceSessionTicketId(ticketId: number | null): void {
    this.voiceSessionTicketId = ticketId;
  }

  async handleToolCall(toolCall: any): Promise<ToolCallResult> {
    const functionCalls = toolCall.functionCalls || [];
    const functionResponses = [];
    let uiAction: ToolCallResult['uiAction'];
    let uiActionToolCallId: string | undefined;
    let uiActionData: any;

    const mockResults: Record<string, string> = {
      add_address: 'Address was added successfully.',
      add_credit_card: 'Credit card was added successfully.',
      schedule_service: 'Service was scheduled successfully.',
    };

    for (const fc of functionCalls) {
      if (fc.name === 'prompt_password_change') {
        uiAction = 'prompt_password_change';
        uiActionToolCallId = fc.id;
        continue;
      }

      if (fc.name === 'send_transcript') {
        uiAction = 'send_transcript';
        uiActionToolCallId = fc.id;
        uiActionData = fc.args;
        continue;
      }

      if (fc.name === 'log_transcript') {
        uiAction = 'log_transcript';
        uiActionToolCallId = fc.id;
        uiActionData = fc.args;
        continue;
      }

      if (fc.name === 'get_conversation_history') {
        uiAction = 'get_conversation_history';
        uiActionToolCallId = fc.id;
        continue;
      }

      if (fc.name === 'log_conversation') {
        this.logVoiceConversation(fc.args?.user_message, fc.args?.model_response);
        functionResponses.push({ id: fc.id, name: fc.name, response: { result: 'logged' } });
        continue;
      }

      if (fc.name === 'update_profile') {
        const result = await this.handleUpdateProfile(fc.args);
        functionResponses.push({ id: fc.id, name: fc.name, response: { result } });
        continue;
      }

      if (fc.name === 'add_asset') {
        uiAction = 'confirm_add_asset';
        uiActionToolCallId = fc.id;
        uiActionData = fc.args;
        continue;
      }

      if (mockResults[fc.name]) {
        await new Promise(resolve => setTimeout(resolve, 10000));
      }
      const result = mockResults[fc.name] || executeToolCall(fc, null);
      functionResponses.push({
        id: fc.id,
        name: fc.name,
        response: { result: `${result} Please inform the user.` },
      });
    }

    return { functionResponses, uiAction, uiActionToolCallId, uiActionData };
  }

  async changePassword(password: string): Promise<void> {
    await this.auth.changePassword({
      password,
      confirm_password: password,
      current_password: 'GuestPass123!',
    });
  }

  private async handleUpdateProfile(args: any): Promise<string> {
    try {
      const payload: any = {
        profile: {
          first_name: args?.first_name || '',
          last_name: args?.last_name || '',
          email: args?.email || '',
          phone: args?.phone || '',
        },
      };
      await this.client.saveClientSettings(payload);
      localStorage.setItem('firstName', payload.profile.first_name);
      localStorage.setItem('lastName', payload.profile.last_name);
      localStorage.setItem('email', payload.profile.email);
      return 'Profile was updated successfully. Please inform the user.';
    } catch (err: any) {
      console.error('[Gemini] Failed to update profile:', err);
      return `Failed to update profile: ${err?.error?.message || err?.message || 'Unknown error'}. Please inform the user.`;
    }
  }

  async addAsset(args: any): Promise<string> {
    try {
      if (!args?.category_id) {
        console.error('[Gemini] No category_id resolved for:', args?.name);
        return `Failed to add asset: could not find a matching category for "${args?.name}". Please inform the user.`;
      }
      const payload = {
        address_ids: [args?.address_id],
        room_objects: [{ category_id: args.category_id }],
      };
      await this.genome.addRoomObject(payload);
      return 'Asset was added successfully. Please inform the user.';
    } catch (err: any) {
      console.error('[Gemini] Failed to add asset:', err);
      return `Failed to add asset: ${err?.error?.message || err?.message || 'Unknown error'}. Please inform the user.`;
    }
  }

  async resolveCategoryId(assetName: string, assetCategory: string): Promise<number | null> {
    try {
      const categories = await this.genome.getCategories();
      const categoryList = categories.map((c: any) => ({ id: c.id, name: c.name }));

      const response = await this.llm.suggest({
        systemPrompt: `You are a category matcher for home assets. Given an asset name and description, pick the BEST matching category from the provided list. You MUST always return a category_id - never fail to match. If no category is a close match, pick the most reasonable general one (e.g. "Other", "Furniture", "Kitchen Applicance"). Also return the new_category_name ONLY if you believe none of the existing categories are appropriate and a new one should be created.`,
        prompt: `Asset: "${assetName}" (${assetCategory || 'no description'})\n\nAvailable categories:\n${JSON.stringify(categoryList)}`,
        responseSchema: {
          type: 'object',
          properties: {
            category_id: { type: 'number', description: 'The ID of the best matching category. Always provide one.' },
            new_category_name: { type: 'string', description: 'Only set this if no existing category fits and a new one should be created. Otherwise omit.' },
          },
          required: ['category_id'],
        },
      });

      const resolvedId = response?.result?.category_id;
      return resolvedId ?? null;
    } catch (err) {
      console.error('[Gemini] LLM category resolution failed:', err);
      return null;
    }
  }

  private logVoiceConversation(_userMessage: string, _modelResponse: string): void {
    // if (!this.voiceSessionTicketId) {
    //   console.log('[Gemini] No voice session ticket ID, skipping log');
    //   return;
    // }
    // const ticketId = this.voiceSessionTicketId;
    // const parts = [];
    // if (userMessage) {
    //   parts.push(`User message: ${userMessage}`);
    // }
    // if (modelResponse) {
    //   parts.push(`Agent message: ${modelResponse}`);
    // }
    // if (parts.length > 0) {
    //   this.conciergeQueries.addCommentMutation.mutateAsync({
    //     support_ticket_id: ticketId,
    //     content: parts.join('\n'),
    //     attachments: [],
    //   }).catch(err => console.error('[Gemini] Failed to log conversation:', err));
    // }
  }
}
